// src/App.js
import React from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { AuthProvider } from "./contexts/AuthContext";

import ProtectedRoute from "./components/ProtectedRoute";

// Pages
import SignIn from "./Login";
import Dashboard from "./Pages/Dashboard/Dashboard";
import CreateProject from "./Pages/CreateProject/CreateProject";
import ProjectDashboard from "./Pages/ProjectDashboard/ProjectDashboard";
import Error500 from "./Pages/Error505";
import CreateTaskModal from "./components/CreateTaskModal/CreateTaskModal";
import ListView from "./components/ListView/ListView";
import UpgradePlan from "./Pages/UpgradePlan";

// Layout components
import Navbar from "./components/NavBar/Navbar";
import Sidebar from "./components/Sidebar/Sidebar";
import Watermark from "./components/Watermark/Watermark";
import ProjectSkeleton from "./components/ProjectSkeleton/ProjectSkeleton";
import MarketplaceSidebar from "./components/MarketplaceSidebar/MarketPlaceSidebar";

import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "./App.scss";

/**
 * AppLayout — common layout for protected pages
 *
 * `showMarketplace` renders MarketplaceSidebar as a true layout
 * sibling (alongside Sidebar), matching how the left sidebar is
 * structured — not nested inside .main-content's scrollable flow.
 * Previously pages did <AppLayout><Page /><MarketplaceSidebar /></AppLayout>,
 * which put both inside the single {children} slot of <main>.
 */
const AppLayout = ({ children, showMarketplace = false }) => {
  return (
    <div className="app">
      <Watermark text="HRLabs" />
      <Navbar />

      <div className="main-area">
        <Sidebar />

        <div className="content-with-marketplace">
          <main className="main-content">{children}</main>
        </div>

        {showMarketplace && <MarketplaceSidebar />}
      </div>
    </div>
  );
};

function App() {
  return (
    <Router>
      <AuthProvider>
        <Routes>
          {/* ===================== */}
          {/* PUBLIC ROUTES */}
          {/* ===================== */}
          <Route path="/" element={<SignIn />} />

          {/* ===================== */}
          {/* PROTECTED ROUTES */}
          {/* ===================== */}
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <AppLayout showMarketplace>
                  <Dashboard />
                </AppLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/ps"
            element={
              <ProtectedRoute>
                <AppLayout>
                  <ProjectSkeleton />
                </AppLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/create"
            element={
              <ProtectedRoute>
                <AppLayout>
                  <CreateProject />
                </AppLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/task/create"
            element={
              <ProtectedRoute>
                <AppLayout>
                  <CreateTaskModal />
                </AppLayout>
              </ProtectedRoute>
            }
          />

          {/* ===================== */}
          {/* PMT ROUTES (MODULE-GATED) */}
          {/* ===================== */}
          <Route
            path="/project/:projectId"
            element={
              <ProtectedRoute>
                <AppLayout showMarketplace>
                  <ProjectDashboard />
                </AppLayout>
              </ProtectedRoute>
            }
          />

          {/* ===================== */}
          {/* UPGRADE PLAN */}
          {/* ===================== */}
          <Route
            path="/upgrade-plan"
            element={
              <ProtectedRoute>
                <AppLayout>
                  <UpgradePlan />
                </AppLayout>
              </ProtectedRoute>
            }
          />

          {/* ===================== */}
          {/* LIST VIEW */}
          {/* ===================== */}
          <Route
            path="/listview"
            element={
              <ProtectedRoute>
                <AppLayout>
                  <ListView />
                </AppLayout>
              </ProtectedRoute>
            }
          />

          {/* ===================== */}
          {/* ERROR / FALLBACK */}
          {/* ===================== */}
          <Route path="/error-500" element={<Error500 />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </AuthProvider>

      {/* Toast notifications */}
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
    </Router>
  );
}

export default App;
